import { fetchEventSource } from '@microsoft/fetch-event-source'
import { AxiosError } from 'axios'
import { EventBus } from 'features/cantileverAISystem/components/eventBus'
import {
  AIResponseEvent,
  makeAIResponseEventDef,
} from 'features/cantileverAISystem/components/makeAiResponseEventDef'
import { AIResponseEventName } from 'features/cantileverAISystem/eventName'
import z, { ZodType } from 'zod'

const EntryDataSchema = z.looseObject({
  id: z.string(),
  type: z.string().optional(),
  data: z.record(z.string(), z.any()),
})

const ToolResultEntriesContentSchema = z.object({
  entries: z.union([z.array(EntryDataSchema), z.number()]),
  entry_count: z.number().optional(),
})

const PineconeResultSchema = z
  .object({
    embedding: z.array(z.number()).optional(),
    metadata: z.record(z.string(), z.any()).optional(),
    score: z.number().optional(),
    re_rank_used: z.boolean().optional(),
    type: z.string().optional(),
    visible_name: z.string().optional(),
  })
  .catchall(z.unknown())

const ToolResultEmbeddingsContentSchema = z
  .object({
    success: z.boolean().optional(),
    results: z.array(PineconeResultSchema),
  })
  .catchall(z.unknown())

const ToolResultErrorContentSchema = z.object({
  error: z.string(),
})

const ToolResultContentSchema = z.union([
  ToolResultEntriesContentSchema,
  ToolResultEmbeddingsContentSchema,
  ToolResultErrorContentSchema,
])

const BaseStreamingEventSchema = z.object({
  session_id: z
    .string()
    .meta({ description: 'Session ID for the streaming conversation' }),
})

const StreamingThoughtEventSchema = BaseStreamingEventSchema.extend({
  type: z.literal('thought').meta({ description: 'Event type identifier' }),
  content: z.string().meta({ description: 'The thought text from the LLM' }),
})

const StreamingToolCallEventSchema = BaseStreamingEventSchema.extend({
  type: z.literal('tool_call').meta({ description: 'Event type identifier' }),
  function_name: z
    .string()
    .meta({ description: 'Name of the function being called' }),
  content: z
    .string()
    .meta({ description: 'Description of the tool call with arguments' }),
})

const StreamingToolResultEventSchema = BaseStreamingEventSchema.extend({
  type: z.literal('tool_result').meta({ description: 'Event type identifier' }),
  function_name: z
    .string()
    .meta({ description: 'Name of the function that was called' }),
  content: ToolResultContentSchema.meta({
    description: 'The response content from the tool execution',
  }),
})

const StreamingErrorEventSchema = BaseStreamingEventSchema.extend({
  type: z.literal('error').meta({ description: 'Event type identifier' }),
  error: z.string().meta({ description: 'Error message or description' }),
})

export const OnStreamEventSchema = z.union([
  StreamingThoughtEventSchema,
  StreamingToolCallEventSchema,
  StreamingToolResultEventSchema,
  StreamingErrorEventSchema,
])

export const isPineconeResult = (
  result: z.infer<typeof ToolResultContentSchema>
): result is z.infer<typeof ToolResultEmbeddingsContentSchema> => {
  return 'results' in result && result.results.length > 0
}

export type ToolResultEntriesArrayContent = {
  entries: z.infer<typeof EntryDataSchema>[]
  entry_count?: number
}

export const isEntryResult = (
  result: z.infer<typeof ToolResultContentSchema>
): result is ToolResultEntriesArrayContent => {
  return (
    'entries' in result &&
    Array.isArray(result.entries) &&
    result.entries.length > 0
  )
}

export type HandlePromptDelphiParams = {
  enrichedPrompt: string
  originalUserPrompt: string
  availableResponseEvents?: ZodType<Omit<AIResponseEvent, 'sessionId'>>[]
  sessionId?: string
  onError?: (error: string) => void
  onStream?: (event: z.infer<typeof OnStreamEventSchema>) => void
}

const CitationSchema = z
  .object({
    entry_id: z.string().meta({
      description: "Entry ID of the cited document (e.g., 'DOC-3a38912b')",
    }),
    file_ref_path: z.string().meta({
      description:
        "Path or filename of the cited file (e.g., 'Everything_We_Know_About_QBOP_Hoods.pdf')",
    }),
    page_number: z.number().meta({
      description: 'Page number within the source document (e.g., 1)',
    }),
    section: z.string().meta({
      description:
        "Section or heading of the cited content (e.g., 'System Overview')",
    }),
    quotations: z.array(z.string()).meta({
      description: 'Direct quotations from the cited source.',
    }),
  })
  .transform((data) => {
    return {
      entryId: data.entry_id,
      fileRefPath: data.file_ref_path,
      pageNumber: data.page_number,
      section: data.section,
      quotations: data.quotations,
    }
  })

export type Citation = z.infer<typeof CitationSchema>

const NaturalLanguageResponseSegmentSchema = z.object({
  citations: z.array(CitationSchema).nullish().meta({
    description: 'The citation for the segment, if applicable',
  }),
  text: z.string().meta({
    description:
      'The response text of the segment grounded by the citation, if applicable',
  }),
})

export type NaturalLanguageResponseSegment = z.infer<
  typeof NaturalLanguageResponseSegmentSchema
>

export const delphiResponseEventDef = makeAIResponseEventDef({
  eventName: AIResponseEventName.DelphiResponse,
  eventDataSchemaWithDescriptions: z
    .object({
      naturalLanguageResponse: z
        .string()
        .meta({ description: 'The natural language response from the LLM' }),
      naturalLanguageResponseBySegments: z
        .array(NaturalLanguageResponseSegmentSchema)
        .meta({
          description: "The agent's response text by segments with citations",
        }),
    })
    .meta({
      description: 'The chatbot response from the LLM',
    }),
})
export class AiControllerV4 {
  constructor(
    private eventBus: EventBus,
    private authorization?: string,
    private LLM_SERVICE?: string,
    private userId?: string
  ) {}

  setConfig({
    authorization,
    LLM_SERVICE,
    userId,
  }: Partial<{
    authorization: string
    LLM_SERVICE: string
    userId: string
  }>) {
    this.authorization = authorization
    this.LLM_SERVICE = LLM_SERVICE
    this.userId = userId
  }

  async handlePromptDelphi({
    enrichedPrompt,
    originalUserPrompt,
    availableResponseEvents,
    sessionId,
    onError,
    onStream,
  }: HandlePromptDelphiParams) {
    try {
      const {
        naturalLanguageResponse,
        naturalLanguageResponseBySegments,
        additionalResponseEvents,
        sessionId: responseSessionId,
      } = await this.sendDelphiQuery({
        sessionId,
        enrichedPrompt,
        originalUserPrompt,
        onError,
        onStream,
        availableResponseEvents,
      })
      this.eventBus.send({
        eventName: delphiResponseEventDef.eventName,
        data: {
          naturalLanguageResponse,
          naturalLanguageResponseBySegments,
        },
        sessionId: responseSessionId,
      })
      for (const event of additionalResponseEvents || []) {
        this.eventBus.send({
          ...event,
          sessionId: responseSessionId,
        })
      }
    } catch (error) {
      if (error instanceof Error) {
        onError?.(error.message)
      } else if (error instanceof z.ZodError) {
        onError?.(z.prettifyError(error))
      } else if (error instanceof AxiosError) {
        onError?.(error.message)
      }
    }
  }

  async sendDelphiQuery({
    sessionId,
    enrichedPrompt,
    originalUserPrompt,
    onError,
    onStream,
    availableResponseEvents,
  }: {
    sessionId?: string
    enrichedPrompt: string
    originalUserPrompt?: string
    onError?: HandlePromptDelphiParams['onError']
    onStream?: HandlePromptDelphiParams['onStream']
    availableResponseEvents?: HandlePromptDelphiParams['availableResponseEvents']
  }) {
    const aiResponseEventsSchema = availableResponseEvents
      ? z.array(z.union(availableResponseEvents))
      : z.array(z.never())
    const request = {
      user_id: this.userId,
      session_id: sessionId,
      new_message: {
        role: 'user',
        parts: [{ text: enrichedPrompt }],
      },
      response_events_schema: z.toJSONSchema(aiResponseEventsSchema),
      original_user_prompt: originalUserPrompt,
    }

    const StreamingResponseEventSchema = BaseStreamingEventSchema.extend({
      type: z
        .literal('response')
        .meta({ description: 'Event type identifier' }),
      natural_language_response: z
        .string()
        .meta({ description: 'The natural language response from the LLM' }),
      natural_language_response_by_segments: z
        .array(NaturalLanguageResponseSegmentSchema)
        .meta({
          description: "The agent's response text by segments with citations",
        }),
      additional_response_events: aiResponseEventsSchema,
    })

    const StreamingEventSchema = z.union([
      OnStreamEventSchema,
      StreamingResponseEventSchema,
    ])

    const endpoint = `${this.LLM_SERVICE}/api/v4/delphi/query`
    let responseEvents: z.infer<typeof StreamingResponseEventSchema> | undefined
    await fetchEventSource(endpoint, {
      method: 'POST',
      body: JSON.stringify(request),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `${this.authorization}`,
      },
      onmessage(rawStreamingEvent) {
        const parseResult = StreamingEventSchema.safeParse(
          JSON.parse(rawStreamingEvent.data)
        )
        if (!parseResult.success) {
          return
        }

        const parsedStreamingEvent = parseResult.data
        if (parsedStreamingEvent.type === 'response') {
          responseEvents = parsedStreamingEvent
        } else if (parsedStreamingEvent.type === 'error') {
          onError?.(parsedStreamingEvent.error)
        } else {
          onStream?.(parsedStreamingEvent)
        }
      },
      onerror(error) {
        onError?.(error.message)
      },
    })

    return {
      naturalLanguageResponse: responseEvents?.natural_language_response,
      naturalLanguageResponseBySegments:
        responseEvents?.natural_language_response_by_segments,
      additionalResponseEvents: responseEvents?.additional_response_events,
      sessionId: responseEvents?.session_id,
    }
  }
}
